<!-- biome-ignore-all lint/a11y/noStaticElementInteractions: the edit row only delegates Escape bubbling up from the TextInput and the action buttons, which are the real controls -->
<script setup lang="ts">
  import { computed, nextTick, ref, useTemplateRef } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import Button from '../../Button/Button.vue';
  import { ButtonSize, ButtonVariant } from '../../Button/Button.types';
  import { FieldSize } from '../FormField';
  import TextInput from '../TextInput/TextInput.vue';
  import { resolveInputTransform } from '../inputTransforms';
  import {
    actionsClass,
    editRootClass,
    fieldClass,
    rootClass,
    valueClass,
  } from './InlineEditInput.css';
  import type {
    InlineEditInputEmits,
    InlineEditInputProps,
  } from './InlineEditInput.types';

  type ButtonInstance = { $el?: HTMLElement };

  const props = withDefaults(defineProps<InlineEditInputProps>(), {
    modelValue: null,
    emptyLabel: '-',
    editIconName: TUNDR_ICONS.PEN,
    disabled: false,
  });

  const emit = defineEmits<InlineEditInputEmits>();

  const { t } = useLocale('ds-components');

  const isEditing = ref(false);
  const draft = ref('');
  const error = ref<string | undefined>(undefined);
  const loading = ref(false);

  const inputRef = useTemplateRef<InstanceType<typeof TextInput>>('inputRef');
  const triggerRef = useTemplateRef<ButtonInstance>('triggerRef');

  const currentValue = computed(() => props.modelValue ?? '');

  const displayValue = computed(() =>
    currentValue.value.trim() ? currentValue.value : props.emptyLabel,
  );

  const resolvedAriaLabel = computed(
    () => props.ariaLabel ?? t('inline_edit_input.input_aria_label'),
  );

  const resolvedEditAriaLabel = computed(
    () => props.editAriaLabel ?? t('inline_edit_input.edit_aria_label'),
  );

  const transformValue = (value: string) =>
    props.transform ? resolveInputTransform(props.transform)(value) : value;

  const focusTrigger = () => triggerRef.value?.$el?.focus();

  const focusInput = () => inputRef.value?.inputRef?.focus();

  const resolveErrorMessage = (reason: unknown) => {
    if (typeof reason === 'string' && reason) {
      return reason;
    }
    if (reason instanceof Error && reason.message) {
      return reason.message;
    }
    return t('inline_edit_input.confirm_error');
  };

  const startEditing = () => {
    if (props.disabled || isEditing.value) {
      return;
    }
    draft.value = transformValue(currentValue.value);
    error.value = props.validate?.(draft.value);
    isEditing.value = true;
    emit('editingChange', true);
  };

  const stopEditing = async () => {
    isEditing.value = false;
    draft.value = '';
    error.value = undefined;
    emit('editingChange', false);
    await nextTick();
    focusTrigger();
  };

  const onInput = (value: string) => {
    draft.value = transformValue(value);
    error.value = props.validate?.(draft.value);
  };

  const onConfirmClick = async () => {
    if (loading.value) {
      return;
    }

    const value = draft.value;
    const validationError = props.validate?.(value);

    if (validationError) {
      error.value = validationError;
      return;
    }

    if (value === currentValue.value) {
      await stopEditing();
      return;
    }

    loading.value = true;
    error.value = undefined;

    try {
      await props.onConfirm(value);
      emit('update:modelValue', value);
      await stopEditing();
    } catch (reason) {
      error.value = resolveErrorMessage(reason);
      await nextTick();
      focusInput();
    } finally {
      loading.value = false;
    }
  };

  const onCancelClick = async () => {
    if (loading.value) {
      return;
    }
    emit('cancel');
    await stopEditing();
  };
</script>

<template>
  <div
    v-if="isEditing"
    :class="[rootClass, editRootClass]"
    @keydown.esc.stop="onCancelClick"
  >
    <TextInput
      ref="inputRef"
      :class="fieldClass"
      :model-value="draft"
      :size="FieldSize.SM"
      :name="name"
      :test-id="testId"
      :placeholder="placeholder"
      :aria-label="resolvedAriaLabel"
      :error="error"
      :disabled="loading"
      autofocus
      @update:model-value="onInput"
      @keydown.enter.prevent="onConfirmClick"
    />

    <div :class="actionsClass">
      <Button
        :variant="ButtonVariant.Icon"
        :size="ButtonSize.SM"
        is-ghost
        :icon-name="TUNDR_ICONS.CHECK"
        :aria-label="t('inline_edit_input.confirm_aria_label')"
        :disabled="!!error"
        :loading="loading"
        @click="onConfirmClick"
      />
      <Button
        :variant="ButtonVariant.Icon"
        :size="ButtonSize.SM"
        is-ghost
        :icon-name="TUNDR_ICONS.CLOSE"
        :aria-label="t('inline_edit_input.cancel_aria_label')"
        :disabled="loading"
        @click="onCancelClick"
      />
    </div>
  </div>

  <div v-else :class="rootClass">
    <span :class="valueClass">{{ displayValue }}</span>

    <Button
      ref="triggerRef"
      :variant="ButtonVariant.Icon"
      :size="ButtonSize.SM"
      is-ghost
      :icon-name="editIconName"
      :aria-label="resolvedEditAriaLabel"
      :disabled="disabled"
      :disabled-reason="disabledReason"
      @click="startEditing"
    />
  </div>
</template>
